import React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';

// AIDEV-NOTE: Low-level Radix DropdownMenu wrapper, parallel to ui/Tooltip/Tooltip.tsx and ui/Popover/Popover.tsx.
// Most callsites should use TCIDropdown (./TCIDropdown.tsx) which sets project defaults. Reach for this
// primitive directly only when the opinionated wrapper is the wrong shape (e.g. you need a submenu, or
// you're composing items into something other than a single trigger + content list).

export type IWidth = 'default' | 'small' | 'unset';

const contentVariants = tv({
  base: [
    'tw-z-9999 tw-rounded tw-border tw-bg-white tw-text-popover-foreground tw-shadow-md tw-outline tw-outline-1',
    'tw-outline-gray-300 tw-py-2 tw-font-tci',
  ],
  variants: {
    width: {
      default: 'tw-min-w-[10rem]',
      small: 'tw-min-w-[6rem]',
      unset: 'tw-min-w-0',
    },
  },
});

// AIDEV-NOTE: Item styling resets descendant <button>/<a>/<i> chrome so consumers can wrap arbitrary
// existing trigger components (which often render a <button> with their own styling) and still get a
// uniform menu row.
const itemVariants = tv({
  base: [
    'tw-flex tw-items-center tw-gap-2 tw-mx-1 tw-rounded tw-px-5 tw-py-1 tw-text-sm tw-text-very-dark-grey',
    'tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left tw-no-underline tw-outline-none',
    // menu items are not inline text links — suppress the global a:hover/a:focus underline
    'hover:tw-no-underline focus:tw-no-underline',
    'data-[highlighted]:tw-bg-light-grey data-[disabled]:tw-opacity-50 data-[disabled]:tw-cursor-not-allowed',
    'data-[highlighted]:tw-ring-2 data-[highlighted]:tw-ring-inset data-[highlighted]:tw-ring-tci-purple',
    '[&_button]:tw-bg-transparent [&_button]:tw-border-none [&_button]:tw-p-0 [&_button]:tw-w-full',
    '[&_button]:tw-text-left [&_button]:tw-cursor-pointer [&_button]:tw-text-inherit',
    '[&_a]:tw-text-inherit [&_a]:tw-no-underline [&_a]:tw-w-full [&_a]:tw-block',
    '[&_a:hover]:tw-no-underline [&_a:focus]:tw-no-underline',
    '[&_i]:tw-text-tci-purple [&_i]:tw-text-lg [&_i]:tw-mr-2',
  ],
});

interface DropdownContentProps {
  container?: HTMLElement | null;
  width?: IWidth;
}

const DropdownContent = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Content>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & DropdownContentProps
>(({
  align = 'center',
  children,
  className,
  container,
  side = 'bottom',
  sideOffset = 4,
  width = 'default',
  ...props
}, ref) => (
  <DropdownMenuPrimitive.Portal container={container}>
    <DropdownMenuPrimitive.Content
      ref={ref}
      align={align}
      className={cn(
        contentVariants({ width }),
        `data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out
        data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95
        data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-2
        data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2
        data-[side=top]:tw-slide-in-from-bottom-2`,
        className,
      )}
      side={side}
      sideOffset={sideOffset}
      {...props}
    >
      {children}
    </DropdownMenuPrimitive.Content>
  </DropdownMenuPrimitive.Portal>
));

DropdownContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownItem = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Item>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(itemVariants(), className)}
    {...props}
  />
));

DropdownItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownSeparator = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('tw-my-1 tw-h-px tw-bg-light-grey', className)}
    {...props}
  />
));

DropdownSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownLabel = React.forwardRef<
React.ElementRef<typeof DropdownMenuPrimitive.Label>,
React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('tw-px-6 tw-py-1 tw-text-xs tw-font-semibold tw-text-dark-grey', className)}
    {...props}
  />
));

DropdownLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const Dropdown = Object.assign(DropdownMenuPrimitive.Root, {
  Content: DropdownContent,
  Group: DropdownMenuPrimitive.Group,
  Item: DropdownItem,
  Label: DropdownLabel,
  Portal: DropdownMenuPrimitive.Portal,
  Separator: DropdownSeparator,
  Trigger: DropdownMenuPrimitive.Trigger,
});

export default Dropdown;
